import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Paper, Typography, Box, Chip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel, List, ListItem, ListItemText } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PushPinIcon from '@mui/icons-material/PushPin';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useDraggable, useDroppable, DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { formatConnectionTime, getCategoryStyle, isIcelandAirFlight } from '../../utils/connectionUtils';
import { useAppTheme } from '../../contexts/ThemeContext';
import '../../FlightDataPage.css';

const getOrdinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

const bagLabel = (n) => `${n || 0} ${(n || 0) === 1 ? 'Bag' : 'Bags'}`;

// The visual for one Edit-modal Connections row. Used both in-place (wrapped by
// SortableDestinationItem) and inside the DragOverlay portal. A plain flex row
// (NOT a MUI ListItem — its absolutely-positioned action mis-paints under the
// drag transform). Row 0 is the flight's primary destination and gets the gold
// box. When `overlay`, the row is opaque so Safari composites the floating copy
// on a clean solid layer (the fix for the drag ghosting/repaint trails).
function DestinationRow({
  dest, index, isPrimary, isLast, onDelete,
  setNodeRef, style, dragProps, isDragging = false, overlay = false,
}) {
  const freightText = dest.freight && dest.freight.length > 0
    ? `Freight: ${dest.freight.map((f) => `${f.type} ${f.pieces || f.count}/${f.weight || 0}`).join(', ')}`
    : null;
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...(dragProps || {})}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        boxSizing: 'border-box',
        py: 1,
        px: 0.5,
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
        userSelect: 'none',
        backgroundColor: overlay
          ? (isPrimary ? 'rgb(58, 52, 34)' : 'rgb(26, 33, 45)')
          : (isPrimary ? 'rgba(249, 182, 18, 0.08)' : 'transparent'),
        ...(overlay ? { boxShadow: '0 8px 20px rgba(0,0,0,0.45)', borderRadius: '8px' } : {}),
        ...(isPrimary
          ? { border: '2px solid #F9B612', borderRadius: '8px' }
          : {
              border: '2px solid transparent',
              borderBottom: !isLast ? '1px solid rgba(255,255,255,0.1)' : '2px solid transparent',
            }),
      }}
    >
      <DragIndicatorIcon fontSize="small" sx={{ color: '#888', flexShrink: 0 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ color: '#fff', fontWeight: isPrimary ? 700 : 400, lineHeight: 1.3 }}>
          {`${dest.city}: ${bagLabel(dest.bags)}`}
        </Typography>
        {freightText && (
          <Typography sx={{ color: '#ccc', fontSize: '0.75rem', lineHeight: 1.2 }}>
            {freightText}
          </Typography>
        )}
      </Box>
      <IconButton
        aria-label="delete"
        onClick={overlay ? undefined : () => onDelete(index)}
        onPointerDown={(e) => e.stopPropagation()}
        sx={{ color: '#f44336', flexShrink: 0 }}
      >
        <DeleteIcon />
      </IconButton>
    </Box>
  );
}

// In-place sortable row. While it's the one being dragged we hide it (opacity 0)
// and let the DragOverlay copy be what moves — that's the dnd-kit pattern that
// fixes Safari's transform ghosting inside the modal.
function SortableDestinationItem({ id, dest, index, isPrimary, isLast, onDelete }) {
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id });
  // No in-place transform/transition. The dragged row is hidden (opacity 0) and
  // the DragOverlay copy is the only thing that moves — the list rows never
  // transform in place. That's what finally kills Safari's repaint ghosting,
  // which trailed off ANY in-place transform near the drag (including the Notes
  // field below). Rows snap to the new order on drop.
  const style = {
    opacity: isDragging ? 0 : 1,
  };
  return (
    <DestinationRow
      dest={dest}
      index={index}
      isPrimary={isPrimary}
      isLast={isLast}
      onDelete={onDelete}
      setNodeRef={setNodeRef}
      style={style}
      dragProps={{ ...attributes, ...listeners }}
      isDragging={isDragging}
    />
  );
}

function FlightTile({ flight, sourceColumnId, params, isDragOverlay = false, hideColdGates = false, onFlightEdit, onPinClick, availablePinPositions, onSplitLocal, onGhostConfirm, onGhostDismiss }) {
  const { currentTheme } = useAppTheme();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const tileRef = useRef(null);
  const prevSourceColumnId = useRef(sourceColumnId);

  const [editedFlight, setEditedFlight] = useState({
    flightNumber: flight.flightNumber || flight.originalFlightNumber || '',
    destination: flight.destination || '',
    etd: flight.etd || '',
    gate: flight.gate || '',
    notes: flight.notes || '',
    carousel: flight.carousel || '',
    connectionCategory: flight.connectionCategory || 'normal',
    destinations: flight.destinations ? [...flight.destinations] : [], // Deep copy array
    allFreight: flight.allFreight ? [...flight.allFreight] : [] // Deep copy array
  });

  // Pointer sensor for the drag-to-reorder connections list in the edit modal.
  // A small activation distance keeps the delete buttons and clicks working.
  const destinationSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );
  // City of the connection currently being dragged — drives the DragOverlay.
  const [activeDestCity, setActiveDestCity] = useState(null);

  // Trigger pulse animation when component mounts and has multiple same-destination flights
  useEffect(() => {
    if (flight.hasMultipleToSameDestination && !isDragOverlay) {
      // Use IntersectionObserver to detect when tile comes into view
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setIsPulsing(true);
              // Stop pulsing after animation completes (3 pulses = 2.4 seconds)
              setTimeout(() => setIsPulsing(false), 2400);
            }
          });
        },
        { threshold: 0.5 }
      );

      if (tileRef.current) {
        observer.observe(tileRef.current);
      }

      return () => {
        if (tileRef.current) {
          observer.unobserve(tileRef.current);
        }
      };
    }
  }, [flight.hasMultipleToSameDestination, isDragOverlay]);

  // Trigger pulse animation when assigned to a driver
  useEffect(() => {
    if (flight.hasMultipleToSameDestination && !isDragOverlay) {
      // Check if the tile was just moved to a driver column
      if (prevSourceColumnId.current === 'unassigned' && sourceColumnId !== 'unassigned') {
        setIsPulsing(true);
        // Stop pulsing after animation completes (3 pulses = 2.4 seconds)
        setTimeout(() => setIsPulsing(false), 2400);
      }
      prevSourceColumnId.current = sourceColumnId;
    }
  }, [sourceColumnId, flight.hasMultipleToSameDestination, isDragOverlay]);

  // Carousel assignments based on gate - Revised 9/4/2026
  const carouselAssignments = {
    23: 4, 24: 3, 25: 5, 26: 2, 27: 6, 28: 1, 29: 4, 31: 3, 33: 5, 35: 2, 37: 6, 39: 1,
    40: 1, 41: 6, 42: 2, 43: 5, 44: 3, 45: 4, 46: 1, 47: 6, 48: 5, 49: 3, 50: 4, 51: 3,
    52: 5, 53: 2, 54: 6, 55: 1, 56: 4, 57: 3, 58: 5, 59: 2, 60: 6, 61: 1, 62: 4, 63: 3,
    64: 5, 65: 2, 66: 6, 67: 1
  };

  const getCarouselForGate = (gate) => {
    if (!gate) return '';
    const gateNum = parseInt(gate.replace(/[A-Z]/gi, ''), 10);
    return carouselAssignments[gateNum] || '';
  };

  const { attributes, listeners, setNodeRef: setDraggableRef, transform, isDragging } = useDraggable({
    id: flight.uniqueId,
    data: { flight, sourceColumnId },
    disabled: isDragOverlay,
  });

  const { setNodeRef: setDroppableRef, isOver: isDropOver } = useDroppable({
    id: `tile-drop-${flight.uniqueId}`,
    data: { flight, sourceColumnId },
    disabled: isDragOverlay,
  });

  // Check if this is an Iceland Air flight
  const isIcelandAir = isIcelandAirFlight(flight.flightNumber || flight.originalFlightNumber);
  
  // Check if flight is cancelled
  const isCancelled = flight.status?.toLowerCase().includes('cancel') || false;
  
  // Use international category for Iceland Air flights, cancelled for cancelled flights, freight-only for freight tiles, otherwise use connection category
  const category = isCancelled ? 'cancelled' :
                   isIcelandAir ? 'international' :
                   flight.isLocal ? 'local' :
                   (flight.isFreightOnly || flight.connectionCategory === 'freight-only') ? 'freight-only' :
                   (flight.isOvernight && !(flight.isFreightOnly || flight.connectionCategory === 'freight-only')) ? 'overnight' :
                   (flight.connectionCategory || 'normal');
  
  const categoryStyles = getCategoryStyle(category);
  const isSubmarineTheme = currentTheme === 'submarine';
  const isOrbitTheme = currentTheme === 'orbit';
  // Corporate renders no-category tiles as light glass — text and the
  // neutral (white-on-dark) chips flip to dark ink there
  const isGlassTile = currentTheme === 'corporate' && category === 'normal';
  const isLocalTile = flight.isLocal && !flight.isLocalFreight;
  const baseBackgroundColor = categoryStyles.backgroundColor || 'transparent';

  const paperStyle = {
    transform: isDragOverlay ? undefined : CSS.Translate.toString(transform),
    touchAction: 'none',
    backgroundColor: baseBackgroundColor,
    '--original-bg': baseBackgroundColor,
  };
  // A tile needs attention when it carries something — bags OR freight — bound
  // for a city OTHER than its own primary destination, i.e. a real downline
  // transfer the dispatcher should confirm. Examples:
  //   • bags to STL + onward SJU on one flight (which city is primary?)
  //   • a freight split whose freight rides MCO then transfers onward to ISP
  // Bags that simply terminate at the primary (e.g. an MCO bag tile with no
  // onward transfer) do NOT pulse. Heavy "-H" rows resolve to their base city.
  // Cleared once the dispatcher opens Edit and Saves (sets primaryAddressed).
  const primaryCity = (flight.destination || '').replace(/-H$/, '').trim().toUpperCase();
  const hasDownlineTransfer = !!primaryCity && (flight.destinations || []).some((d) => {
    const hasContent = (d.bags || 0) > 0 || (d.freight && d.freight.length > 0);
    const city = (d.city || '').replace(/-H$/, '').trim().toUpperCase();
    return hasContent && city && city !== primaryCity;
  });
  // primaryConfirmed = the backend reconciled this flight's primary against
  // Brock's per-flight dest — nothing left for a human to confirm. The pulse
  // now only fires on the ~1% of multi-dest tiles Brock couldn't resolve.
  const needsPrimaryAttention =
    hasDownlineTransfer && !flight.primaryAddressed && !flight.primaryConfirmed && !isDragOverlay;

  // Edit-modal save button: when a flagged (pulsing) tile is opened and nothing
  // has been changed, the button reads "Confirm" (green) — one tap to
  // acknowledge the downline is handled. The moment the dispatcher edits
  // anything, it reverts to the normal blue "Save".
  const editFieldsSnapshot = (src) => JSON.stringify({
    flightNumber: src.flightNumber ?? '',
    destination: src.destination ?? '',
    etd: src.etd ?? '',
    gate: src.gate ?? '',
    notes: src.notes ?? '',
    carousel: src.carousel ?? '',
    connectionCategory: src.connectionCategory ?? 'normal',
    destinations: src.destinations ?? [],
    allFreight: src.allFreight ?? [],
  });
  const pristineEdit = {
    flightNumber: flight.flightNumber || flight.originalFlightNumber || '',
    destination: flight.destination || '',
    etd: flight.etd || '',
    gate: flight.gate || '',
    notes: flight.notes || '',
    carousel: flight.carousel || '',
    connectionCategory: flight.connectionCategory || 'normal',
    destinations: flight.destinations || [],
    allFreight: flight.allFreight || [],
  };
  const isEditDirty = editFieldsSnapshot(editedFlight) !== editFieldsSnapshot(pristineEdit);
  const showConfirm = needsPrimaryAttention && !isEditDirty;

  // Provisional ghost: bags Brock scanned that the TDR has no row for.
  // Dashed + muted until the dispatcher confirms it into the plan.
  const isUnconfirmedGhost = Boolean(flight.isGhost && !flight.ghostConfirmed);
  if (isUnconfirmedGhost) {
    paperStyle.border = '2px dashed rgba(255, 255, 255, 0.55)';
    paperStyle.opacity = 0.9;
    paperStyle.filter = 'saturate(0.5)';
  }

  const tileClassName = [
    'flight-tile',
    category ? `flight-tile--${category}` : null,
    isDragOverlay ? 'flight-tile--drag-overlay' : null,
    flight.isSplit ? 'flight-tile--split' : null,
    flight.isLocal ? 'flight-tile--local' : null,
    needsPrimaryAttention ? 'tile-needs-primary' : null,
    isUnconfirmedGhost ? 'flight-tile--ghost' : null,
  ].filter(Boolean).join(' ');
  const displayFlightNumber = flight.originalFlightNumber || `${flight.flightNumber}`;

  // --- NEW: Check for high bag count ---
  const isHighBagCount = (flight.bags || 0) >= 15;
  
  // Calculate FWD/AFT bag distribution from binDistribution
  const getFwdAftBagCounts = () => {
    const fwdBins = ['A', 'B', 'C'];
    const aftBins = ['D', 'E', 'F'];
    
    let fwdBags = 0;
    let aftBags = 0;
    
    // If this is a split tile, show all bags in the respective compartment
    if (flight.isSplit && flight.splitType) {
      if (flight.splitType === 'FWD') {
        return { fwdBags: flight.bags || 0, aftBags: 0 };
      } else if (flight.splitType === 'AFT') {
        return { fwdBags: 0, aftBags: flight.bags || 0 };
      }
    }
    
    // For unsplit tiles, calculate from binDistribution if available
    if (flight.binDistribution && Object.keys(flight.binDistribution).length > 0) {
      Object.entries(flight.binDistribution).forEach(([bin, count]) => {
        if (fwdBins.includes(bin)) {
          fwdBags += count || 0;
        } else if (aftBins.includes(bin)) {
          aftBags += count || 0;
        }
      });
    }
    
    return { fwdBags, aftBags };
  };
  
  // Calculate FWD/AFT freight distribution from freightByBin
  const getFwdAftFreightCounts = () => {
    const fwdBins = ['A', 'B', 'C'];
    const aftBins = ['D', 'E', 'F'];
    
    let fwdFreight = 0;
    let aftFreight = 0;
    
    if (flight.freightByBin && Object.keys(flight.freightByBin).length > 0) {
      Object.entries(flight.freightByBin).forEach(([bin, count]) => {
        if (fwdBins.includes(bin)) {
          fwdFreight += count || 0;
        } else if (aftBins.includes(bin)) {
          aftFreight += count || 0;
        }
      });
    }
    
    return { fwdFreight, aftFreight };
  };
  
  const { fwdBags, aftBags } = getFwdAftBagCounts();
  const { fwdFreight, aftFreight } = getFwdAftFreightCounts();
  const hasBinData = flight.binDistribution && Object.keys(flight.binDistribution).length > 0;
  const hasFreightBinData = flight.freightByBin && Object.keys(flight.freightByBin).length > 0;

  const totalFreightPieces = (flight.allFreight || []).reduce((total, item) => {
    const pieceCount = parseInt(item.count ?? item.pieces ?? 0, 10);
    return total + (Number.isNaN(pieceCount) ? 0 : pieceCount);
  }, 0);

  const freightCount = (flight.isSplit && hasFreightBinData)
    ? (flight.splitType === 'FWD' ? fwdFreight :
       flight.splitType === 'AFT' ? aftFreight : totalFreightPieces)
    : totalFreightPieces;

  const freightItems = freightCount > 0 ? (flight.allFreight || []) : [];

  const handleEditClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Initialize edit state with current flight data
    setEditedFlight({
      flightNumber: flight.flightNumber || flight.originalFlightNumber || '',
      destination: flight.destination || '',
      etd: flight.etd || '',
      gate: flight.gate || '',
      notes: flight.notes || '',
      carousel: flight.carousel || '',
      connectionCategory: flight.connectionCategory || 'normal',
      destinations: flight.destinations ? JSON.parse(JSON.stringify(flight.destinations)) : [],
      allFreight: flight.allFreight ? JSON.parse(JSON.stringify(flight.allFreight)) : []
    });
    setEditModalOpen(true);
  };

  // The primary destination must be one of the flight's connections. Enforced
  // only for multi-destination flights — a single-destination flight keeps the
  // freeform field so a mis-parsed city can still be corrected. Heavy "-H" rows
  // match their base city. To route to a city that isn't a connection here, the
  // dispatcher makes a custom tile (there's no add-connection in this modal).
  const destinationIsValid = (() => {
    const dests = editedFlight.destinations || [];
    if (dests.length <= 1) return true;
    const cities = dests.map((d) => (d.city || '').replace(/-H$/, '').trim().toUpperCase());
    const typed = (editedFlight.destination || '').replace(/-H$/, '').trim().toUpperCase();
    return cities.includes(typed);
  })();

  const handleEditSave = () => {
    // Block saving a primary that isn't one of the connections; the Destination
    // field shows the inline error explaining why.
    if (!destinationIsValid) return;
    if (onFlightEdit) {
      // Pass the edited data to parent, let it calculate the new connection time
      onFlightEdit(flight.uniqueId, editedFlight);
    }
    setEditModalOpen(false);
  };

  const handleEditCancel = useCallback(() => {
    setEditModalOpen(false);
    setEditedFlight({
      flightNumber: flight.flightNumber || flight.originalFlightNumber || '',
      destination: flight.destination || '',
      etd: flight.etd || '',
      gate: flight.gate || '',
      notes: flight.notes || '',
      carousel: flight.carousel || '',
      connectionCategory: flight.connectionCategory || 'normal',
      destinations: flight.destinations ? JSON.parse(JSON.stringify(flight.destinations)) : [],
      allFreight: flight.allFreight ? JSON.parse(JSON.stringify(flight.allFreight)) : []
    });
  }, [flight.flightNumber, flight.originalFlightNumber, flight.destination, flight.etd, flight.gate, flight.notes, flight.carousel, flight.connectionCategory, flight.destinations, flight.allFreight]);

  // Delete a destination/connection
  const handleDeleteDestination = (index) => {
    setEditedFlight(prev => ({
      ...prev,
      destinations: prev.destinations.filter((_, i) => i !== index)
    }));
  };

  // Delete a freight item
  const handleDeleteFreight = (index) => {
    setEditedFlight(prev => ({
      ...prev,
      allFreight: prev.allFreight.filter((_, i) => i !== index)
    }));
  };

  // Heavy-bag "-H" split rows never lead — they belong below their base city.
  // If the top row is a "-H" split and any non-heavy row exists, promote the
  // first non-heavy row to primary. A flight with ONLY heavy bags keeps -H on
  // top (its base city is the real destination).
  const enforceNonHeavyPrimary = (destinations) => {
    if (!destinations.length) return destinations;
    if (!/-H$/.test(destinations[0].city || '')) return destinations;
    const firstNonHeavy = destinations.findIndex((d) => !/-H$/.test(d.city || ''));
    if (firstNonHeavy <= 0) return destinations;
    return arrayMove(destinations, firstNonHeavy, 0);
  };

  // Move the connection whose city matches `city` (case-insensitive) to the
  // top so it becomes the primary destination. No-op if nothing matches or
  // it's already on top — used by the Destination text field (type-to-set),
  // so typing "STL" pops the STL row into the gold box.
  const promoteDestinationToPrimary = (destinations, city) => {
    const target = (city || '').trim().toUpperCase();
    if (!target) return destinations;
    const idx = destinations.findIndex((d) => (d.city || '').toUpperCase() === target);
    if (idx <= 0) return destinations;
    return enforceNonHeavyPrimary(arrayMove(destinations, idx, 0));
  };

  // Drag-to-reorder the connections list. Sortable ids are the destination
  // city — stable and tied to the data, which keeps dnd-kit's drag animation
  // smooth (index-based ids are a dnd-kit anti-pattern and broke the layout
  // mid-drag). Row 0 is the primary; the "-H" suffix is stripped for the
  // primary string, and a -H row is kept out of the top slot unless heavy-only.
  const handleDestinationDragEnd = (event) => {
    const { active, over } = event;
    setActiveDestCity(null);
    if (!over || active.id === over.id) return;
    setEditedFlight((prev) => {
      const oldIndex = prev.destinations.findIndex((d) => d.city === active.id);
      const newIndex = prev.destinations.findIndex((d) => d.city === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      const reordered = enforceNonHeavyPrimary(arrayMove(prev.destinations, oldIndex, newIndex));
      const primaryCity = (reordered[0]?.city || prev.destination).replace(/-H$/, '');
      return { ...prev, destinations: reordered, destination: primaryCity };
    });
  };

  const createChipSx = (backgroundColor, labelColor, overrides = {}) => {
    const {
      backgroundColor: overrideBackgroundColor,
      background: overrideBackground,
      color: overrideTextColor,
      '& .MuiChip-label': chipLabelOverrides = {},
      ...restOverrides
    } = overrides;

    const resolvedBackground = overrideBackgroundColor || overrideBackground || backgroundColor;
    const resolvedLabelColor = overrideTextColor || labelColor;

    return {
      background: `${resolvedBackground} !important`,
      backgroundColor: `${resolvedBackground} !important`,
      color: `${resolvedLabelColor} !important`,
      ...restOverrides,
      '& .MuiChip-label': {
        color: `${resolvedLabelColor} !important`,
        textShadow: 'none !important',
        ...chipLabelOverrides,
      },
    };
  };

  const buttonHighlightColor = currentTheme === 'nightvision'
    ? '#00FF41'
    : isSubmarineTheme
    ? '#00FFAA'
    : isOrbitTheme
    ? '#3ef5ff'
    : (isLocalTile ? '#000' : '#fff');
  const buttonBackgroundHover = currentTheme === 'nightvision'
    ? 'rgba(0, 255, 65, 0.1)'
    : isSubmarineTheme
    ? 'rgba(0, 255, 170, 0.12)'
    : isOrbitTheme
    ? 'rgba(62, 245, 255, 0.12)'
    : (isLocalTile ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)');

  const localPrimaryTextColor = isSubmarineTheme ? '#013d36' : isOrbitTheme ? '#020b16' : '#000';

  const getLocalChipOverrides = (type) => {
    if (!isLocalTile || (!isSubmarineTheme && !isOrbitTheme)) return {};

    if (isOrbitTheme) {
      const sharedOrbit = {
        '& .MuiChip-label': {
          fontWeight: 700,
          letterSpacing: '0.03em',
        },
      };

      if (type === 'bags') {
        return {
          color: '#020b16 !important',
          '& .MuiChip-label': {
            fontWeight: 700,
            letterSpacing: '0.03em',
            color: '#020b16 !important',
          },
        };
      }

      return sharedOrbit;
    }

    const shared = {
      border: '1px solid rgba(0, 120, 110, 0.25) !important',
      boxShadow: '0 0 8px rgba(0, 255, 170, 0.25) !important',
      '& .MuiChip-label': {
        fontWeight: 700,
        letterSpacing: '0.03em',
      },
    };

    switch (type) {
      case 'bags':
        return {
          ...shared,
          background: 'rgba(0, 190, 150, 0.3)',
          color: '#012a24',
        };
      case 'fwd':
        return {
          ...shared,
          border: '1px solid rgba(0, 170, 255, 0.35) !important',
          background: 'rgba(0, 170, 255, 0.25)',
          color: '#053a63',
        };
      case 'aft':
        return {
          ...shared,
          border: '1px solid rgba(255, 140, 0, 0.35) !important',
          background: 'rgba(255, 140, 0, 0.28)',
          color: '#5b2300',
        };
      default:
        return shared;
    }
  };

  return (
    <Paper
      ref={(node) => {
        setDraggableRef(node);
        setDroppableRef(node);
        tileRef.current = node;
      }}
      style={paperStyle}
      className={tileClassName}
      data-flight-category={category}
      {...listeners}
      {...attributes}
      sx={{
        p: 1.5,
        mb: isDragOverlay ? 0 : 1,
        position: 'relative',
        cursor: isDragOverlay ? 'grabbing' : 'grab',
        border: flight.pinPosition ? 
          `2px solid ${flight.pinPosition === 1 ? '#FFD700' : 
                       flight.pinPosition === 2 ? '#C0C0C0' : 
                       flight.pinPosition === 3 ? '#CD7F32' : 'rgba(255,255,255,0.1)'}` :
          flight.notes ? '2px solid #FFD700' : '1px solid rgba(255,255,255,0.1)',
        borderTop: isDropOver && !isDragOverlay ? '3px solid #2196F3' : undefined,
        boxShadow: flight.pinPosition ?
          `0 0 10px ${flight.pinPosition === 1 ? 'rgba(255, 215, 0, 0.5)' :
                      flight.pinPosition === 2 ? 'rgba(192, 192, 192, 0.5)' :
                      flight.pinPosition === 3 ? 'rgba(205, 127, 50, 0.5)' : 'transparent'}` :
          flight.notes ? '0 0 10px rgba(255, 215, 0, 0.5)' :
          (isDragOverlay ? '0 10px 15px -3px rgba(0,0,0,0.3), 0 4px 6px -2px rgba(0,0,0,0.2)' : 'none'),
        transition: 'none',
        visibility: !isDragOverlay && isDragging ? 'hidden' : 'visible',
        transform: isDragOverlay ? 'scale(1.05)' : 'none',
        '&:hover': (!isDragOverlay && !isSubmarineTheme) ? {
          backgroundColor: category === 'normal' ? '#4a5568' : categoryStyles.backgroundColor,
          opacity: 0.9,
          transform: 'translateY(-2px)',
        } : undefined
      }}
    >
      {/* component="div": this header nests a Chip (a div) when multiple
          tiles share a destination — divs can't live inside Typography's
          default <p> (React DOM-nesting error). */}
      <Typography component="div" sx={{
        color: flight.isLocal ? `${localPrimaryTextColor} !important` : '#fff',
        fontWeight: 'bold',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 0.5
      }}>
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {flight.isLocalFreight ? displayFlightNumber : flight.isLocal ? displayFlightNumber : (
            flight.hasMultipleToSameDestination ? (
              <Chip
                label={`${displayFlightNumber} → ${flight.destination}`}
                size="small"
                className={isPulsing ? 'pulse' : ''}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.95)',
                  color: '#000',
                  fontWeight: 'bold',
                  fontSize: '0.875rem',
                  height: 'auto',
                  '& .MuiChip-label': {
                    padding: '2px 10px',
                    color: '#000 !important',
                  },
                  animation: isPulsing ? 'pulse 0.8s ease-in-out 3' : 'none',
                }}
              />
            ) : (
              `${displayFlightNumber} → ${flight.destination}`
            )
          )}
          {flight.wouldBeColdButForSchedule && flight.sch && !flight.isFreightOnly && category !== 'freight-only' && (
            <Box
              component="span"
              sx={{
                marginLeft: '8px',
                color: '#64b5f6 !important',
                fontSize: '0.9rem',
                textShadow: 'none !important',
              }}
            >
              {flight.sch.replace('L', '')}
            </Box>
          )}
          {flight.isSplit && flight.splitType && (
            <Box
              component="span"
              sx={{
                marginLeft: '8px',
                padding: '2px 6px',
                backgroundColor: flight.splitType === 'FWD' ? '#2196F3' : '#FF9800',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 'bold',
                color: '#000 !important',
                textShadow: 'none !important',
              }}
            >
              {flight.splitType}
            </Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
                color: `${isGlassTile ? '#1c2733' : (categoryStyles.color && !isLocalTile) ? categoryStyles.color : '#fff'} !important`,
                fontWeight: 'bold',
                textShadow: 'none !important',
                // "3h 34m" must never break across lines — when the header
                // row gets crowded, the flight-number side wraps instead.
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ml: 0.5,
              }}
        >
          {flight.isLocalFreight ? '' : flight.isLocal ? (
            <Box
              component="span"
              sx={{
                backgroundColor: '#2196F3',
                color: '#fff !important',
                padding: '2px 8px',
                borderRadius: '4px',
                fontSize: '0.875rem',
                textShadow: 'none !important',
              }}
            >
              Carousel {flight.carousel || getCarouselForGate(flight.gate) || '?'}
            </Box>
          ) : isCancelled ? 'Cancelled' : isIcelandAir ? '' : (flight.displayConnectionTime || formatConnectionTime(flight.connectionTime))}
        </Box>
      </Typography>
      
      {/* Check multi-destination first, then single destination freight */}
      {flight.isMultiDestination && flight.destinations && flight.destinations.length > 0 ? (
        <>
          {/* For freight-only tiles, don't show main freight chip - it will show next to destinations */}
          {flight.isFreightOnly ? (
            <></>
          ) : (
            /* Bag tiles - only show destinations that have bags */
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {flight.primaryConfirmed && !(flight.isSplit ? flight.bags : flight.destinations[0].bags) ? (
                /* Confirmed primary with zero terminating bags (the 2125→BWI
                   pattern): every bag connects onward — say so instead of
                   showing a noisy 0-count chip */
                <Typography sx={{ color: '#aaa', fontSize: '0.72rem', fontStyle: 'italic' }}>
                  all bags connect at {flight.destinations[0].city}
                </Typography>
              ) : (
              <Chip
                label={`${flight.destinations[0].city}: ${bagLabel(flight.isSplit ? flight.bags : flight.destinations[0].bags)}`}
                size="small" 
                sx={createChipSx(
                  flight.isLocal
                    ? ((flight.isSplit ? flight.bags : flight.destinations[0].bags) > 110
                        ? (isSubmarineTheme
                            ? 'linear-gradient(135deg, rgba(255, 90, 90, 0.45), rgba(211, 47, 47, 0.75))'
                            : 'rgba(211, 47, 47, 0.8)')
                        : (isSubmarineTheme ? 'linear-gradient(135deg, rgba(255,255,255,0.92), rgba(210, 240, 236, 0.7))' : 'rgba(0,0,0,0.15)'))
                    : ((flight.isSplit ? flight.bags : flight.destinations[0].bags) >= 15
                        ? (isGlassTile ? 'rgba(198, 40, 40, 0.92)' : 'rgba(255, 0, 0, 0.3)')
                        : (isGlassTile ? 'rgba(28, 39, 51, 0.08)' : 'rgba(255,255,255,0.15)')),
                  flight.isLocal
                    ? ((flight.isSplit ? flight.bags : flight.destinations[0].bags) > 110
                        ? '#fff'
                        : (isSubmarineTheme ? '#013830' : isOrbitTheme ? '#020b16' : '#000'))
                    : ((flight.isSplit ? flight.bags : flight.destinations[0].bags) >= 15
                        ? (isGlassTile ? '#fff' : '#ffcdd2')
                        : (isGlassTile ? '#1c2733' : '#fff')),
                  {
                    fontWeight: (flight.isSplit ? flight.bags : flight.destinations[0].bags) >= 15 ? 'bold' : 'normal',
                    ...getLocalChipOverrides('bags'),
                  }
                )}
              />
              )}
              {/* Show FWD/AFT distribution if we have bin data */}
              {hasBinData && (fwdBags > 0 || aftBags > 0) && (
                <>
                  {fwdBags > 0 && (
                    <Chip
                      label={`FWD: ${fwdBags}`}
                      size="small"
                      sx={createChipSx(
                        flight.isLocal
                          ? (isSubmarineTheme ? 'linear-gradient(135deg, rgba(0, 180, 255, 0.35), rgba(0, 110, 180, 0.4))' : 'rgba(33, 150, 243, 0.6)')
                          : 'rgba(33, 150, 243, 0.3)',
                        flight.isLocal
                          ? (isSubmarineTheme ? '#053a63' : '#0D47A1')
                          : '#2196F3',
                        {
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          ...getLocalChipOverrides('fwd'),
                        }
                      )}
                    />
                  )}
                  {aftBags > 0 && (
                    <Chip
                      label={`AFT: ${aftBags}`}
                      size="small"
                      sx={createChipSx(
                        flight.isLocal
                          ? (isSubmarineTheme ? 'linear-gradient(135deg, rgba(255, 140, 0, 0.35), rgba(180, 70, 0, 0.4))' : 'rgba(255, 152, 0, 0.6)')
                          : 'rgba(255, 152, 0, 0.3)',
                        flight.isLocal
                          ? (isSubmarineTheme ? '#5b2300' : '#BF360C')
                          : '#FF9800',
                        {
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          ...getLocalChipOverrides('aft'),
                        }
                      )}
                    />
                  )}
                </>
              )}
              {flight.isOvernight && !isCancelled && (
                <Chip 
                  label="Overnight" 
                  size="small" 
                  sx={createChipSx('rgba(156, 39, 176, 0.3)', '#9C27B0', { fontWeight: 'bold' })} 
                />
              )}
              {!flight.isLocal && flight.isThruBag && (
                <Chip 
                  label="THRU" 
                  size="small" 
                  sx={createChipSx('rgba(76, 175, 80, 0.3)', '#4CAF50', { fontWeight: 'bold' })} 
                />
              )}
              {/* Ghost tile: bags Brock scanned, no TDR row. Built from the
                  scan record; the chip clears when the dispatcher confirms. */}
              {flight.isGhost && !flight.ghostConfirmed && (
                <Chip
                  label="NOT ON TDR"
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#F9B612', color: '#F9B612', fontWeight: 'bold', fontSize: '0.68rem' }}
                />
              )}
              {/* The reverse: bags on the TDR that Brock never scanned. The
                  seeded tile note carries the driver heads-up; this is the
                  at-a-glance mark. */}
              {flight.brockScanned === false && !flight.isGhost && (
                <Chip
                  label="NO SCANS"
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#FF9800', color: '#FF9800', fontWeight: 'bold', fontSize: '0.68rem' }}
                />
              )}
              {/* Same gate is now shown in notes
              {!flight.isLocal && flight.isSameGate && (
                <Chip 
                  label="Same Gate" 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(0, 188, 212, 0.3)', color: '#00BCD4', fontWeight: 'bold' }} 
                />
              )} */}
            </Box>
          )}
          
          {/* Transfer destinations stacked below */}
          {flight.destinations.length > 1 && (
            <Box sx={{ mt: 1, ml: 2 }}>
              {flight.destinations.slice(1).map((dest, idx) => {
                // For bag-only split tiles, only show destinations with bags > 0
                // For freight-only split tiles, show destinations with freight
                // For original (non-split) tiles, show all destinations
                if ((flight.isSplit || flight.isColdMixedSplit) && !flight.isFreightOnly && dest.bags === 0) {
                  return null; // Hide 0-bag destinations on bag-only split tiles
                }
                if ((flight.isSplit || flight.isColdMixedSplit) && flight.isFreightOnly && (!dest.freight || dest.freight.length === 0)) {
                  return null; // Hide non-freight destinations on freight-only split tiles
                }
                
                return (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255,255,255,0.8) !important',
                        fontSize: '0.875rem',
                        textShadow: 'none !important',
                      }}
                    >
                      ↳ {dest.city}:
                    </Typography>
                    {!flight.isFreightOnly && dest.bags > 0 && (
                    <Chip
                      label={bagLabel(dest.bags)}
                      size="small"
                      sx={createChipSx(
                        isGlassTile ? 'rgba(28, 39, 51, 0.08)' : 'rgba(255,255,255,0.1)',
                        isGlassTile ? '#1c2733' : '#fff', {
                        fontSize: '0.75rem',
                        height: '20px',
                      })}
                    />
                    )}
                    {(flight.isFreightOnly || !flight.isSplit) && dest.freight && dest.freight.length > 0 && (
                      <>
                        {/* Show individual badges for each freight item */}
                        {dest.freight.map((freightItem, freightIdx) => (
                          <Chip
                            key={freightIdx}
                            label={`${freightItem.type} ${freightItem.pieces || freightItem.count}/${freightItem.weight || 0}`}
                            size="small"
                            sx={createChipSx('rgba(249, 182, 18, 0.3)', '#F9B612', {
                              fontWeight: 'bold',
                              fontSize: '0.75rem',
                              height: '20px',
                            })}
                          />
                        ))}
                        {/* Show FWD/AFT distribution if we have freight bin data and this is a freight-only flight */}
                        {hasFreightBinData && flight.isFreightOnly && (fwdFreight > 0 || aftFreight > 0) && (
                          <>
                            {fwdFreight > 0 && (
                              <Chip
                                label={`FWD: ${fwdFreight}`}
                                size="small"
                                sx={createChipSx('rgba(33, 150, 243, 0.3)', '#2196F3', {
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: '20px',
                                })}
                              />
                            )}
                            {aftFreight > 0 && (
                              <Chip
                                label={`AFT: ${aftFreight}`}
                                size="small"
                                sx={createChipSx('rgba(255, 152, 0, 0.3)', '#FF9800', {
                                  fontWeight: 'bold',
                                  fontSize: '0.7rem',
                                  height: '20px',
                                })}
                              />
                            )}
                          </>
                        )}
                      </>
                    )}
                  </Box>
                );
              }).filter(Boolean)}
            </Box>
          )}
        </>
      ) : (flight.isFreightOnly || flight.connectionCategory === 'freight-only') && freightCount > 0 ? (
        /* Single destination freight-only tile */
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {/* Show individual badges for each freight item */}
          {freightItems.length > 0 && freightItems.map((freightItem, idx) => (
            <Chip 
              key={idx}
              label={`${freightItem.type} ${freightItem.pieces || freightItem.count}/${freightItem.weight || 0}`} 
              size="small" 
              sx={createChipSx('rgba(249, 182, 18, 0.3)', '#F9B612', { fontWeight: 'bold' })} 
            />
          ))}
          {/* Show FWD/AFT freight distribution if we have freight bin data */}
          {hasFreightBinData && (fwdFreight > 0 || aftFreight > 0) && (
            <>
              {fwdFreight > 0 && (
                            <Chip
                              label={`FWD: ${fwdFreight}`}
                              size="small"
                              sx={createChipSx('rgba(33, 150, 243, 0.3)', '#2196F3', {
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                              })}
                            />
              )}
              {aftFreight > 0 && (
                            <Chip
                              label={`AFT: ${aftFreight}`}
                              size="small"
                              sx={createChipSx('rgba(255, 152, 0, 0.3)', '#FF9800', {
                                fontWeight: 'bold',
                                fontSize: '0.7rem',
                              })}
                            />
              )}
            </>
          )}
          {flight.isOvernight && !isCancelled && (
            <Chip 
              label="Overnight" 
              size="small" 
              sx={createChipSx('rgba(156, 39, 176, 0.3)', '#9C27B0', { fontWeight: 'bold' })} 
            />
          )}
          {flight.isThruBag && (
            <Chip 
              label="THRU" 
              size="small" 
              sx={createChipSx('rgba(76, 175, 80, 0.3)', '#4CAF50', { fontWeight: 'bold' })} 
            />
          )}
          {/* Same gate is now shown in notes
          {flight.isSameGate && (
            <Chip 
              label="Same Gate" 
              size="small" 
              sx={{ backgroundColor: 'rgba(0, 188, 212, 0.3)', color: '#00BCD4', fontWeight: 'bold' }} 
            />
          )} */}
        </Box>
      ) : (
        /* Single destination display */
        <>
          {!(flight.isFreightOnly || flight.connectionCategory === 'freight-only') && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Chip 
                label={flight.isLocal ? bagLabel(flight.bags) : `${flight.destination}: ${bagLabel(flight.bags)}`}
                size="small" 
                sx={createChipSx(
                  flight.isLocal
                    ? ((flight.bags || 0) > 110
                        ? (isSubmarineTheme
                            ? 'linear-gradient(135deg, rgba(255, 90, 90, 0.45), rgba(211, 47, 47, 0.75))'
                            : 'rgba(211, 47, 47, 0.8)')
                        : (isSubmarineTheme ? 'linear-gradient(135deg, rgba(255,255,255,0.92), rgba(210, 240, 236, 0.7))' : 'rgba(0,0,0,0.15)'))
                    : (isHighBagCount
                        ? (isGlassTile ? 'rgba(198, 40, 40, 0.92)' : 'rgba(255, 0, 0, 0.3)')
                        : (isGlassTile ? 'rgba(28, 39, 51, 0.08)' : 'rgba(255,255,255,0.15)')),
                  flight.isLocal
                    ? ((flight.bags || 0) > 110
                        ? '#fff'
                        : (isSubmarineTheme ? '#013830' : '#000'))
                    : (isHighBagCount
                        ? (isGlassTile ? '#fff' : '#ffcdd2')
                        : (isGlassTile ? '#1c2733' : '#fff')),
                  {
                    fontWeight: flight.isLocal ? 'bold' : (isHighBagCount ? 'bold' : 'normal'),
                    ...getLocalChipOverrides('bags'),
                  }
                )} 
              />
              {/* Show FWD/AFT distribution if we have bin data */}
              {hasBinData && (fwdBags > 0 || aftBags > 0) && (
                <>
                  {fwdBags > 0 && (
                    <Chip
                      label={`FWD: ${fwdBags}`}
                      size="small"
                      sx={createChipSx(
                        flight.isLocal
                          ? (isSubmarineTheme ? 'linear-gradient(135deg, rgba(0, 180, 255, 0.35), rgba(0, 110, 180, 0.4))' : 'rgba(33, 150, 243, 0.6)')
                          : 'rgba(33, 150, 243, 0.3)',
                        flight.isLocal
                          ? (isSubmarineTheme ? '#053a63' : '#0D47A1')
                          : '#2196F3',
                        {
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          ...getLocalChipOverrides('fwd'),
                        }
                      )}
                    />
                  )}
                  {aftBags > 0 && (
                    <Chip
                      label={`AFT: ${aftBags}`}
                      size="small"
                      sx={createChipSx(
                        flight.isLocal
                          ? (isSubmarineTheme ? 'linear-gradient(135deg, rgba(255, 140, 0, 0.35), rgba(180, 70, 0, 0.4))' : 'rgba(255, 152, 0, 0.6)')
                          : 'rgba(255, 152, 0, 0.3)',
                        flight.isLocal
                          ? (isSubmarineTheme ? '#5b2300' : '#BF360C')
                          : '#FF9800',
                        {
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                          ...getLocalChipOverrides('aft'),
                        }
                      )}
                    />
                  )}
                </>
              )}
              {freightCount > 0 && (
             <>
               {/* Show individual badges for each freight item */}
               {freightItems.length > 0 && freightItems.map((freightItem, idx) => (
                 <Chip 
                   key={idx}
                   label={`${freightItem.type} ${freightItem.pieces || freightItem.count}/${freightItem.weight || 0}`} 
                   size="small" 
                   sx={createChipSx('rgba(249, 182, 18, 0.3)', '#F9B612', { fontWeight: 'bold' })} 
                 />
               ))}
                 {/* Show FWD/AFT freight distribution if we have freight bin data */}
                 {hasFreightBinData && (fwdFreight > 0 || aftFreight > 0) && (
                 <>
                   {fwdFreight > 0 && (
                     <Chip
                       label={`FWD: ${fwdFreight}`}
                       size="small"
                       sx={createChipSx('rgba(33, 150, 243, 0.3)', '#2196F3', {
                         fontWeight: 'bold',
                         fontSize: '0.7rem',
                       })}
                     />
                   )}
                   {aftFreight > 0 && (
                     <Chip
                       label={`AFT: ${aftFreight}`}
                       size="small"
                       sx={createChipSx('rgba(255, 152, 0, 0.3)', '#FF9800', {
                         fontWeight: 'bold',
                         fontSize: '0.7rem',
                       })}
                     />
                   )}
                 </>
               )}
             </>
              )}
              {flight.isOvernight && !isCancelled && (
                <Chip 
                  label="Overnight" 
                  size="small" 
                  sx={createChipSx('rgba(156, 39, 176, 0.3)', '#9C27B0', { fontWeight: 'bold' })} 
                />
              )}
              {!flight.isLocal && flight.isThruBag && (
                <Chip 
                  label="THRU" 
                  size="small" 
                  sx={createChipSx('rgba(76, 175, 80, 0.3)', '#4CAF50', { fontWeight: 'bold' })} 
                />
              )}
              {/* Ghost tile: bags Brock scanned, no TDR row. Built from the
                  scan record; the chip clears when the dispatcher confirms. */}
              {flight.isGhost && !flight.ghostConfirmed && (
                <Chip
                  label="NOT ON TDR"
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#F9B612', color: '#F9B612', fontWeight: 'bold', fontSize: '0.68rem' }}
                />
              )}
              {/* The reverse: bags on the TDR that Brock never scanned. The
                  seeded tile note carries the driver heads-up; this is the
                  at-a-glance mark. */}
              {flight.brockScanned === false && !flight.isGhost && (
                <Chip
                  label="NO SCANS"
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: '#FF9800', color: '#FF9800', fontWeight: 'bold', fontSize: '0.68rem' }}
                />
              )}
              {/* Same gate is now shown in notes
              {!flight.isLocal && flight.isSameGate && (
                <Chip 
                  label="Same Gate" 
                  size="small" 
                  sx={{ backgroundColor: 'rgba(0, 188, 212, 0.3)', color: '#00BCD4', fontWeight: 'bold' }} 
                />
              )} */}
            </Box>
          )}
        </>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 1 }}>
        <Box sx={{ flex: 1 }}>
          {!flight.isLocalFreight && (
            <Typography
              variant="body2"
              sx={{
                color: `${flight.isLocal ? (isSubmarineTheme ? '#02554b' : isOrbitTheme ? '#020b16' : '#000') : 'lightgrey'} !important`,
                textShadow: 'none !important',
              }}
            >
              {flight.isLocal ?
                `ETA: ${flight.etd || 'N/A'} | Gate: ${flight.gate || 'Terminal'}` :
                `ETD: ${isCancelled ? '' : flight.etd} | Gate: ${
                  isCancelled ? 'Reroutes' :
                  ((flight.isFreightOnly || flight.connectionCategory === 'freight-only') && flight.isOvernight) ? 'Stays at Gate' :
                  (flight.isOvernight && !(flight.isFreightOnly || flight.connectionCategory === 'freight-only')) ? 'Reroutes' :
                  isIcelandAir || (hideColdGates && category === 'cold' && !((flight.isFreightOnly || flight.connectionCategory === 'freight-only') && flight.isOvernight)) ? 'Cold Sort' :
                  flight.gate
                }`
              }
            </Typography>
          )}
          {flight.notes && (
            <Box
              sx={{
                mt: 0.5,
                color: '#FFD700 !important',
                fontSize: '0.8rem',
                fontWeight: '500',
                textShadow: 'none !important',
              }}
            >
              Note: {flight.notes}
            </Box>
          )}
        </Box>
        {!isDragOverlay && (
          <Box
            sx={{ 
              pointerEvents: 'auto',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 0.5
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {flight.runOrder && (
              <Box
                sx={{
                  backgroundColor: '#2196F3',
                  color: '#fff !important',
                  fontWeight: 'bold',
                  fontSize: '0.7rem',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  minWidth: '24px',
                  textAlign: 'center',
                  lineHeight: 1.4,
                }}
              >
                {getOrdinal(flight.runOrder)}
              </Box>
            )}
            {onPinClick && !flight.isLocal && !flight.isLocalFreight && !flight.runOrder && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onPinClick(flight.uniqueId);
                }}
                sx={{
                  backgroundColor: flight.pinPosition ? 
                    (flight.pinPosition === 1 ? '#FFD700' :
                     flight.pinPosition === 2 ? '#C0C0C0' :
                     flight.pinPosition === 3 ? '#CD7F32' : 'transparent') 
                    : 'transparent',
                  color: flight.pinPosition ? '#000' : 'rgba(255, 255, 255, 0.7)',
                  padding: '2px 6px',
                  minWidth: 'unset',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: flight.pinPosition ?
                      (flight.pinPosition === 1 ? '#FFD700' :
                       flight.pinPosition === 2 ? '#C0C0C0' :
                       flight.pinPosition === 3 ? '#CD7F32' : 'rgba(255, 255, 255, 0.1)')
                      : 'rgba(255, 255, 255, 0.1)',
                    color: flight.pinPosition ? '#000' : '#fff'
                  }
                }}
                disableRipple
              >
                {flight.pinPosition ? 
                  (flight.pinPosition === 1 ? '1st' :
                   flight.pinPosition === 2 ? '2nd' :
                   flight.pinPosition === 3 ? '3rd' : <PushPinIcon fontSize="small" />)
                  : <PushPinIcon fontSize="small" />}
              </IconButton>
            )}
            {flight.isLocal && onSplitLocal && (
              <IconButton
                size="small"
                // Interactive control — dropped from the Teams share capture
                // (its inline theme-accent color would also leak into the
                // otherwise theme-neutral image).
                data-share-exclude="true"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onSplitLocal();
                }}
                sx={{
                  color: buttonHighlightColor,
                  filter: currentTheme === 'nightvision' ? 'drop-shadow(0 0 6px rgba(0, 255, 65, 0.8))' : 'none',
                  padding: '4px',
                  minWidth: 'unset',
                  '&:hover': {
                    color: buttonHighlightColor,
                    backgroundColor: buttonBackgroundHover,
                  }
                }}
                disableRipple
              >
                <CallSplitIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              data-share-exclude="true"
              onClick={handleEditClick}
              sx={{
                color: flight.isLocal ? buttonHighlightColor : 'rgba(255, 255, 255, 0.7)',
                filter: flight.isLocal && currentTheme === 'nightvision' ? 'drop-shadow(0 0 6px rgba(0, 255, 65, 0.8))' : 'none',
                padding: '4px',
                minWidth: 'unset',
                '&:hover': {
                  color: flight.isLocal
                    ? buttonHighlightColor
                    : '#fff',
                  backgroundColor: flight.isLocal
                    ? buttonBackgroundHover
                    : 'rgba(255, 255, 255, 0.1)'
                }
              }}
              disableRipple
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      {/* Ghost decisions get their own row under the regular tile content,
          so the ETD, gate, and note lay out exactly like a normal tile.
          Both calls are available wherever the tile sits, and both are
          final — persisted against the inbound, surviving reloads and
          re-pastes. */}
      {isUnconfirmedGhost && !isDragOverlay && (
        <Box
          sx={{ display: 'flex', gap: 1, mt: 1, pointerEvents: 'auto' }}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {onGhostConfirm && (
            <Button
              size="small"
              data-share-exclude="true"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onGhostConfirm(flight.uniqueId);
              }}
              sx={{
                flex: 1,
                backgroundColor: '#4CAF50', color: '#fff', fontWeight: 700,
                fontSize: '0.7rem', lineHeight: 1.2, px: 1, py: 0.4,
                minWidth: 'unset', textTransform: 'none',
                '&:hover': { backgroundColor: '#43A047' },
              }}
              disableRipple
            >
              Confirm
            </Button>
          )}
          {onGhostDismiss && (
            <Button
              size="small"
              data-share-exclude="true"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onGhostDismiss(flight.uniqueId);
              }}
              sx={{
                flex: 1,
                color: 'rgba(255,255,255,0.7)', fontWeight: 700,
                fontSize: '0.7rem', lineHeight: 1.2, px: 1, py: 0.4,
                minWidth: 'unset', textTransform: 'none',
                border: '1px solid rgba(255,255,255,0.3)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff' },
              }}
              disableRipple
            >
              Dismiss
            </Button>
          )}
        </Box>
      )}

      {/* RCS thruster quads — one per side, drag-overlay only. Invisible
          unless a theme styles them; DispatchBoard streams per-side burn
          intensities (--rcs-n/s/e/w) onto the overlay while dragging. */}
      {isDragOverlay && (
        <>
          <Box className="rcs-jet rcs-jet--n" aria-hidden />
          <Box className="rcs-jet rcs-jet--s" aria-hidden />
          <Box className="rcs-jet rcs-jet--e" aria-hidden />
          <Box className="rcs-jet rcs-jet--w" aria-hidden />
        </>
      )}

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        maxWidth="sm" 
        fullWidth
        disablePortal={false}
        keepMounted={false}
        disableEnforceFocus={false}
        disableAutoFocus={false}
        disableRestoreFocus={true}
        onClose={handleEditCancel}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }
          }
        }}
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
          onMouseDown: (e) => e.stopPropagation(),
          onPointerDown: (e) => e.stopPropagation(),
          sx: {
            backgroundColor: 'rgba(28, 37, 50, 0.98)',
            // No backdrop-filter on this modal: a blur here + the drag-reorder
            // CSS transform inside it makes Chrome mis-composite and the layout
            // breaks mid-drag (the tell — it renders fine while DevTools is open,
            // because that forces continuous repaints). The panel is ~98% opaque,
            // so dropping the blur is barely noticeable. (It was already disabled
            // on Windows/Safari for the same class of flicker bug.)
            backdropFilter: 'none',
            WebkitBackdropFilter: 'none',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            color: '#fff',
            position: 'relative',
            zIndex: 1300
          }
        }}
      >
        <DialogTitle sx={{ color: '#fff', backgroundColor: 'transparent' }}>
          Edit Flight {displayFlightNumber}{flight.isLocal ? '' : ` → ${editedFlight.destination}`}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: 'transparent' }}>
          <Box
            component="form"
            id="edit-flight-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleEditSave();
            }}
            sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
          >
            {/* Flight Number */}
            <TextField
              label="Flight Number"
              value={editedFlight.flightNumber}
              onChange={(e) => setEditedFlight(prev => ({ ...prev, flightNumber: e.target.value }))}
              placeholder="e.g., WN 123"
              size="small"
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              inputProps={{
                style: { WebkitUserSelect: 'text', userSelect: 'text' }
              }}
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                },
                '& .MuiInputBase-input': {
                  WebkitUserSelect: 'text',
                  userSelect: 'text'
                }
              }}
            />

            {/* Destination (for non-local flights) */}
            {!flight.isLocal && (
              <TextField
                label="Destination"
                value={editedFlight.destination}
                onChange={(e) => {
                  const value = e.target.value;
                  // Two-way sync: typing a city that exists in the connections
                  // list below promotes it to the top (gold box). A city that
                  // isn't in the list still wins as a freeform primary.
                  setEditedFlight(prev => ({
                    ...prev,
                    destination: value,
                    destinations: promoteDestinationToPrimary(prev.destinations, value),
                  }));
                }}
                error={!destinationIsValid}
                helperText={!destinationIsValid
                  ? `"${(editedFlight.destination || '').trim()}" isn't one of the connections below.`
                  : undefined}
                FormHelperTextProps={{ sx: { color: '#f44336', marginLeft: 0, fontWeight: 600 } }}
                placeholder="e.g., PHX"
                size="small"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                inputProps={{
                  style: { WebkitUserSelect: 'text', userSelect: 'text' }
                }}
                sx={{
                  '& .MuiInputLabel-root': { color: !destinationIsValid ? '#f44336' : '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: !destinationIsValid ? '#f44336' : '#555' },
                    '&:hover fieldset': { borderColor: !destinationIsValid ? '#f44336' : '#777' },
                    '&.Mui-focused fieldset': { borderColor: !destinationIsValid ? '#f44336' : '#2196f3' }
                  },
                  '& .MuiInputBase-input': {
                    WebkitUserSelect: 'text',
                    userSelect: 'text'
                  }
                }}
              />
            )}

            {/* Connection Category Dropdown (for non-local flights) */}
            {!flight.isLocal && (
              <FormControl size="small" sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                },
                '& .MuiSvgIcon-root': { color: '#ccc' }
              }}>
                <InputLabel>Connection Category</InputLabel>
                <Select
                  value={editedFlight.connectionCategory}
                  onChange={(e) => setEditedFlight(prev => ({ ...prev, connectionCategory: e.target.value }))}
                  label="Connection Category"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        backgroundColor: 'rgba(31, 41, 55, 0.98)',
                        color: '#fff',
                        '& .MuiMenuItem-root': {
                          color: '#fff',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(33, 150, 243, 0.3)',
                            '&:hover': {
                              backgroundColor: 'rgba(33, 150, 243, 0.4)'
                            }
                          }
                        }
                      }
                    }
                  }}
                >
                  <MenuItem value="normal">Normal</MenuItem>
                  <MenuItem value="hot">Hot</MenuItem>
                  <MenuItem value="priority">Priority</MenuItem>
                  <MenuItem value="cold">Cold</MenuItem>
                  <MenuItem value="freight-only">Freight-Only</MenuItem>
                  <MenuItem value="international">International</MenuItem>
                  <MenuItem value="overnight">Overnight</MenuItem>
                  <MenuItem value="thru">Thru</MenuItem>
                </Select>
              </FormControl>
            )}

            {!flight.isLocal ? (
              <>
                <TextField
                  label="ETD"
                  value={editedFlight.etd}
                  onChange={(e) => setEditedFlight(prev => ({ ...prev, etd: e.target.value }))}
                  placeholder="e.g., 10:30"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  inputProps={{
                    style: { WebkitUserSelect: 'text', userSelect: 'text' }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { color: '#ccc' },
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: '#777' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    },
                    '& .MuiInputBase-input': {
                      WebkitUserSelect: 'text',
                      userSelect: 'text'
                    }
                  }}
                />
                <TextField
                  label="Gate"
                  value={editedFlight.gate}
                  onChange={(e) => setEditedFlight(prev => ({ ...prev, gate: e.target.value }))}
                  placeholder="e.g., C42"
                  size="small"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                  inputProps={{
                    style: { WebkitUserSelect: 'text', userSelect: 'text' }
                  }}
                  sx={{
                    '& .MuiInputLabel-root': { color: '#ccc' },
                    '& .MuiOutlinedInput-root': {
                      color: '#fff',
                      '& fieldset': { borderColor: '#555' },
                      '&:hover fieldset': { borderColor: '#777' },
                      '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                    },
                    '& .MuiInputBase-input': {
                      WebkitUserSelect: 'text',
                      userSelect: 'text'
                    }
                  }}
                />
              </>
            ) : (
              <TextField
                label="Carousel"
                value={editedFlight.carousel || getCarouselForGate(flight.gate) || ''}
                onChange={(e) => setEditedFlight(prev => ({ ...prev, carousel: e.target.value }))}
                placeholder="e.g., 1, 2, 3, or 4"
                size="small"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                inputProps={{
                  style: { WebkitUserSelect: 'text', userSelect: 'text' }
                }}
                sx={{
                  '& .MuiInputLabel-root': { color: '#ccc' },
                  '& .MuiOutlinedInput-root': {
                    color: '#fff',
                    '& fieldset': { borderColor: '#555' },
                    '&:hover fieldset': { borderColor: '#777' },
                    '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                  },
                  '& .MuiInputBase-input': {
                    WebkitUserSelect: 'text',
                    userSelect: 'text'
                  }
                }}
              />
            )}

            {/* Destinations/Connections List — drag to reorder; the top row is
                the primary destination (gold box) and stays in sync with the
                Destination field above (drag updates it; typing reorders here). */}
            {editedFlight.destinations && editedFlight.destinations.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>
                  Connections ({editedFlight.destinations.length}) — drag to reorder, top is primary
                </Typography>
                <DndContext
                  sensors={destinationSensors}
                  collisionDetection={closestCenter}
                  onDragStart={(event) => setActiveDestCity(event.active.id)}
                  onDragEnd={handleDestinationDragEnd}
                  onDragCancel={() => setActiveDestCity(null)}
                >
                  <SortableContext
                    items={editedFlight.destinations.map((d) => d.city)}
                    strategy={verticalListSortingStrategy}
                  >
                    <Box sx={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: '4px' }}>
                      {editedFlight.destinations.map((dest, index) => (
                        <SortableDestinationItem
                          key={dest.city}
                          id={dest.city}
                          dest={dest}
                          index={index}
                          isPrimary={index === 0}
                          isLast={index === editedFlight.destinations.length - 1}
                          onDelete={handleDeleteDestination}
                        />
                      ))}
                    </Box>
                  </SortableContext>
                  {/* The moving row renders here, in a portal at the document
                      root — outside the modal's compositing context — so Safari
                      paints it cleanly with no ghost trails. */}
                  <DragOverlay>
                    {activeDestCity ? (() => {
                      const i = editedFlight.destinations.findIndex((d) => d.city === activeDestCity);
                      const d = editedFlight.destinations[i];
                      return d ? (
                        <DestinationRow
                          dest={d}
                          index={i}
                          isPrimary={i === 0}
                          isLast={i === editedFlight.destinations.length - 1}
                          isDragging
                          overlay
                        />
                      ) : null;
                    })() : null}
                  </DragOverlay>
                </DndContext>
              </Box>
            )}

            {/* Freight List */}
            {editedFlight.allFreight && editedFlight.allFreight.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ color: '#ccc', mb: 1 }}>
                  Freight ({editedFlight.allFreight.length} items)
                </Typography>
                <List sx={{ backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '8px', padding: 0 }}>
                  {editedFlight.allFreight.map((freight, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        borderBottom: index < editedFlight.allFreight.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                        py: 1
                      }}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteFreight(index)}
                          sx={{ color: '#f44336' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={`${freight.type} ${freight.pieces || freight.count}/${freight.weight || 0}`}
                        primaryTypographyProps={{ sx: { color: '#F9B612', fontWeight: 'bold' } }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <TextField
              label="Notes"
              value={editedFlight.notes}
              onChange={(e) => setEditedFlight(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="e.g., Advance to earlier PHX flight"
              multiline
              rows={3}
              size="small"
              autoFocus
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onPointerDown={(e) => {
                e.stopPropagation();
              }}
              onKeyDown={(e) => {
                // Prevent all key events from bubbling up
                e.stopPropagation();
              }}
              inputProps={{
                style: { WebkitUserSelect: 'text', userSelect: 'text' }
              }}
              sx={{
                '& .MuiInputLabel-root': { color: '#ccc' },
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': { borderColor: '#555' },
                  '&:hover fieldset': { borderColor: '#777' },
                  '&.Mui-focused fieldset': { borderColor: '#2196f3' }
                },
                '& .MuiInputBase-input': {
                  WebkitUserSelect: 'text',
                  userSelect: 'text'
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: 'transparent', zIndex: 9999 }}>
          <Button 
            onClick={handleEditCancel}
            sx={{ 
              color: '#ccc',
              zIndex: 10000,
              pointerEvents: 'auto',
              position: 'relative'
            }}
            onMouseDown={(e) => {
              console.log('Cancel mousedown');
              e.stopPropagation();
            }}
            onPointerDown={(e) => {
              console.log('Cancel pointerdown');
              e.stopPropagation();
            }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="edit-flight-form"
            disabled={!destinationIsValid}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleEditSave();
            }}
            variant="contained"
            sx={{
              backgroundColor: showConfirm ? '#4CAF50' : '#2196f3',
              '&:hover': { backgroundColor: showConfirm ? '#43A047' : '#1976d2' },
              '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.4)' }
            }}
          >
            {showConfirm ? 'Confirm' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default FlightTile;
